import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Shield, Heart, Lock, Target, MessageSquare, FileText, CheckCircle, Users, TrendingUp, Building, Scale, Handshake } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import ProcessStep from "@/components/ProcessStep";
import SectionTitle from "@/components/SectionTitle";
import ContactForm from "@/components/ContactForm";
const Insolvency = () => {
  return <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 md:mb-6 leading-tight px-2">
            ליווי וייצוג משפטי בהליכי חדלות פירעון ושיקום כלכלי
          </h1>
          <h2 className="text-lg sm:text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-6 md:mb-8 leading-relaxed px-2">מתמודדים עם חובות? אנו כאן כדי להוביל אתכם, צעד אחר צעד, לפתיחת דף כלכלי חדש בביטחון.</h2>
          <Link to="/contact">
            <Button size="lg" className="text-base md:text-lg px-6 md:px-8 py-5 md:py-6 w-full sm:w-auto">לקביעת פגישת ייעוץ </Button>
          </Link>
        </div>
      </section>

      {/* Opening Section */}
      <section className="py-12 md:py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <div className="prose prose-base md:prose-lg max-w-none text-right" dir="rtl">
            <p className="text-base md:text-lg leading-relaxed text-foreground mb-3 md:mb-4">
              התמודדות עם חובות היא סיטואציה מורכבת ומלחיצה, המשפיעה על כל תחומי החיים. אנו מבינים את הקושי ואת תחושת חוסר הוודאות.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-foreground mb-3 md:mb-4">
              חוק חדלות פירעון ושיקום כלכלי (שנכנס לתוקף ב-2019 והחליף את פקודת פשיטת הרגל) נועד בדיוק למצבים אלו. מטרתו העיקרית היא לא להעניש, אלא לייצר פתרון הוגן שיאפשר לכם שיקום כלכלי אמיתי ויציאה לדרך חדשה, תוך שמירה על כבודכם.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-foreground font-semibold">
              משרדנו מתמחה בניהול הליכי חדלות פירעון וילווה אתכם במקצועיות, ברגישות ובנחישות עד לקבלת ההפטר המיוחל.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 md:py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <SectionTitle title="השירותים שלנו בתחום חדלות הפירעון" subtitle="אנו מעניקים מעטפת משפטית מלאה ומקיפה בכל שלבי ההליך, הן ליחידים והן לתאגידים" />
          
          <div className="grid md:grid-cols-1 gap-6 md:gap-12 mt-8 md:mt-12">
            {/* Individual Services */}
            <Card className="border-2">
              <CardContent className="p-4 md:p-8">
                <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                  <Users className="w-8 h-8 md:w-10 md:h-10 text-primary flex-shrink-0" />
                  <h3 className="text-xl md:text-2xl font-bold">ליווי וייצוג חייבים (יחידים)</h3>
                </div>
                <ul className="space-y-2 md:space-y-3 text-right" dir="rtl">
                  <li className="flex items-start gap-2 md:gap-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-sm md:text-base"><strong>ייעוץ ראשוני:</strong> בחינת האסטרטגיה המשפטית הנכונה ביותר עבורך.</span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-sm md:text-base"><strong>הגשת בקשה מסודרת:</strong> הכנה והגשה של בקשה לצו פתיחת הליכים.</span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-sm md:text-base"><strong>עיכוב הליכים מיידי:</strong> עם קבלת הצו, אנו פועלים לעצירת כל העיקולים, ההגבלות והטלפונים מנושים.</span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-sm md:text-base"><strong>ייצוג מלא:</strong> ליווי וייצוג שוטף מול הממונה על חדלות פירעון, הנאמן ובית המשפט.</span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-sm md:text-base"><strong>ניהול משא ומתן:</strong> גיבוש הסדרי חוב מול הנושים וגיבוש תוכנית שיקום כלכלי המותאמת ליכולותיך.</span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-sm md:text-base"><strong>קבלת הפטר:</strong> ליווי צמוד עד לקבלת הפטר חובות סופי וסגירת התיק.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Corporate Services */}
            <Card className="border-2">
              <CardContent className="p-4 md:p-8">
                <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                  <Building className="w-8 h-8 md:w-10 md:h-10 text-primary flex-shrink-0" />
                  <h3 className="text-xl md:text-2xl font-bold">חדלות פירעון לתאגידים (חברות)</h3>
                </div>
                <ul className="space-y-2 md:space-y-3 text-right" dir="rtl">
                  <li className="flex items-start gap-2 md:gap-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-sm md:text-base">ייעוץ לדירקטורים ונושאי משרה בחברות בקשיים.</span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-sm md:text-base">הליכי הבראה ושיקום חברות.</span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-sm md:text-base">הקפאת הליכים וגיבוש הסדרי נושים מורכבים.</span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-sm md:text-base">ייצוג בהליכי פירוק חברות מרצון או בכפייה.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Creditor Services */}
            <Card className="border-2">
              <CardContent className="p-4 md:p-8">
                <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                  <Scale className="w-8 h-8 md:w-10 md:h-10 text-primary flex-shrink-0" />
                  <h3 className="text-xl md:text-2xl font-bold">ייצוג נושים</h3>
                </div>
                <ul className="space-y-2 md:space-y-3 text-right" dir="rtl">
                  <li className="flex items-start gap-2 md:gap-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-sm md:text-base">הגשת תביעות חוב מדויקות ומנומקות.</span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-sm md:text-base">מימוש זכויות וייצוג באסיפות נושים.</span>
                  </li>
                  <li className="flex items-start gap-2 md:gap-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-sm md:text-base">ניהול משא ומתן מול החייב ו/או הנאמן להשגת התוצאה המיטבית עבורך.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 md:py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <SectionTitle title="מדוע לבחור במשרדנו לניהול ההליך?" />
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 mt-8 md:mt-12">
            <Card>
              <CardContent className="p-4 md:p-6 text-center">
                <Shield className="w-10 h-10 md:w-12 md:h-12 text-primary mx-auto mb-3 md:mb-4" />
                <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">מקצועיות ללא פשרות</h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  אנו מחזיקים בבקיאות מעמיקה בחוק חדלות פירעון החדש, בפסיקה העדכנית ובתקנות. הידע המקצועי שלנו הוא היתרון שלכם.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 md:p-6 text-center">
                <Heart className="w-10 h-10 md:w-12 md:h-12 text-primary mx-auto mb-3 md:mb-4" />
                <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">ליווי אישי ורגיש</h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  אנו מבינים את המצב הרגיש ומעניקים יחס אישי, סבלני ובגובה העיניים לאורך כל הדרך. אצלנו, אתם לא עוד תיק – אתם לקוחות שחשוב לנו להוביל להצלחה.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 md:p-6 text-center">
                <Lock className="w-10 h-10 md:w-12 md:h-12 text-primary mx-auto mb-3 md:mb-4" />
                <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">דיסקרטיות מובטחת</h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  כל פנייה למשרדנו וכל פרט בתיק מטופלים בסודיות מוחלטת ובחיסיון מלא, מובטח.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 md:p-6 text-center">
                <Target className="w-10 h-10 md:w-12 md:h-12 text-primary mx-auto mb-3 md:mb-4" />
                <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">ממוקדי תוצאות</h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  המטרה שלנו ברורה: להשיג עבורכם את ההפטר המהיר והיעיל ביותר, להגן על הנכסים החשובים לכם, ולפתוח עבורכם דף חדש ונקי מחובות.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-12 md:py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <SectionTitle title="התהליך לפתיחת דף חדש - צעד אחר צעד" />
          
          <div className="space-y-6 md:space-y-8 mt-8 md:mt-12">
            <ProcessStep stepNumber={1} title="שיחת ייעוץ (דיסקרטית)" description="בפגישה ראשונית נבין את תמונת המצב המלאה, ננתח את היקף החובות והנכסים, ונציג בפניך את כל האפשרויות המשפטיות העומדות לרשותך." icon={MessageSquare} />
            <ProcessStep stepNumber={2} title="הכנה והגשת הבקשה" description="לאחר קבלת ההחלטה, נאסוף את כל המסמכים הנדרשים, ננסח ונגיש עבורך בקשה מקצועית ומסודרת לצו פתיחת הליכים." icon={FileText} />
            <ProcessStep stepNumber={3} title="קבלת הצו ועיכוב הליכים" description="עם קבלת הצו, כל ההליכים נגדך מוקפאים. אנו מתחילים לנהל את התיק באופן רשמי מול הממונה והנאמן." icon={Shield} />
            <ProcessStep stepNumber={4} title="ניהול ההליך וגיבוש תוכנית שיקום" description="זוהי תקופת הביניים בה אנו מייצגים אותך בדיונים, מנהלים משא ומתן מול הנושים, ומגבשים יחד תוכנית תשלומים ריאלית המותאמת ליכולותיך." icon={Handshake} />
            <ProcessStep stepNumber={5} title="קבלת הפטר!" description="בסיום התהליך ועם העמידה בתנאי התוכנית, נגיש בקשה למתן הפטר, אשר יפטור אותך מכל החובות ברי התביעה ויאפשר לך לצאת לדרך כלכלית חדשה." icon={TrendingUp} isLast={true} />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <SectionTitle title="שאלות נפוצות בנושא חדלות פירעון" />
          
          <Accordion type="single" collapsible className="w-full mt-8 md:mt-12" dir="rtl">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-right text-base md:text-lg font-semibold">
                מה ההבדל המרכזי בין "פשיטת רגל" ל"חדלות פירעון"?
              </AccordionTrigger>
              <AccordionContent className="text-right text-sm md:text-base leading-relaxed">
                חוק חדלות פירעון החדש שם דגש מרכזי על שיקום כלכלי של החייב, ולא רק על החזר חובות. ההליך מוגבל בזמן (כ-4 שנים בממוצע) ומטרתו להחזיר את האדם למעגל החיים הכלכליים בצורה יעילה ומהירה יותר.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2">
              <AccordionTrigger className="text-right text-base md:text-lg font-semibold">
                מתי מפסיקים הטלפונים מהנושים והעיקולים?
              </AccordionTrigger>
              <AccordionContent className="text-right text-sm md:text-base leading-relaxed">
                מיד עם קבלת הצו לפתיחת הליכים, חל "עיכוב הליכים" אוטומטי. משמעות הדבר היא שאסור לנושים לפעול נגדך באופן עצמאי (כולל עיקולים, הוצאה לפועל וכו') ויש להפנות את כל הפניות אלינו או לנאמן שמונה בתיק.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3">
              <AccordionTrigger className="text-right text-base md:text-lg font-semibold">
                האם אני עלול לאבד את הבית שלי?
              </AccordionTrigger>
              <AccordionContent className="text-right text-sm md:text-base leading-relaxed">
                החוק מעניק הגנה על "דירת מגורים יחידה" בתנאים מסוימים, ובמקרים רבים ניתן להגיע להסדר שיאפשר את השארת הבית או מציאת פתרון דיור הולם. כל מקרה נבחן לגופו ואנו נלחם לשמירה על הנכסים החשובים לך ככל הניתן.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4">
              <AccordionTrigger className="text-right text-base md:text-lg font-semibold">
                האם כל החובות נמחקים בהפטר?
              </AccordionTrigger>
              <AccordionContent className="text-right text-sm md:text-base leading-relaxed">
                ההפטר חל על רוב מוחלט של החובות שנוצרו לפני מתן הצו. הוא אינו חל, בדרך כלל, על חובות מזונות, קנסות פליליים או חובות שנוצרו במרמה. אנו נבחן את הרכב החובות שלך באופן פרטני בפגישת הייעוץ.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA with Contact Form */}
      <section className="py-12 md:py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 md:mb-6 px-2">
              אל תישארו עם החובות לבד. הגיע הזמן לפתוח דף חדש.
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto px-2">
              הצעד הראשון הוא הקשה ביותר, אבל הוא גם החשוב ביותר. אנו מזמינים אתכם לפגישת ייעוץ ראשונית, דיסקרטית וללא כל התחייבות, בה נבחן את מצבכם ונגבש יחד תוכנית פעולה לשיקום כלכלי.
            </p>
          </div>
          
          <ContactForm />
        </div>
      </section>
    </div>;
};
export default Insolvency;